import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, api } from "../lib/api.ts";
import { Icon } from "../shell/icons.tsx";

/**
 * Entrar / Criar conta, without passwords: a 6-digit code by e-mail, or Google.
 * Left, the showcase: floating mockups that change with the phrase below them.
 * Right, the form in steps. Sign-up creates the account when the code is
 * confirmed; the store comes next (the app's "Nova loja" screen).
 */

type Mode = "login" | "signup";
type Step = "email" | "dados" | "codigo";


const TICK = ["Monte as telas da sua loja arrastando blocos.", "Peça à sua IA: ela monta, você aprova.", "Pix integrado e 14 dias grátis."];

export const COMPANY_HEAD: ReactNode = <>Quase <em>lá</em>.</>;

const HEAD: Record<Step, ReactNode> = {
  email: <>Bom te ver de <em>volta</em>.</>,
  dados: <>Sua loja, do <em>seu jeito</em>.</>,
  codigo: <>Confira seu <em>e-mail</em>.</>,
};

/** Messages for `?erro=` (the Google flow comes back to the app with one). */
const BACK_ERRORS: Record<string, string> = {
  google: "Não deu para entrar com o Google. Tente de novo ou use o código por e-mail.",
  "google-cancelado": "Entrada com o Google cancelada.",
  "google-indisponivel": "Entrar com o Google ainda não está disponível. Use o código por e-mail.",
};

const emailOk = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);
const reduceMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

function initialMode(): Mode {
  const q = new URLSearchParams(location.search);
  return q.has("criar-conta") ? "signup" : "login";
}

export function AuthScreen({ onIn }: { onIn: (token: string) => void }) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [step, setStep] = useState<Step>(mode === "signup" ? "dados" : "email");
  // An invitation link brings the invited e-mail: ?criar-conta&email=…
  const [email, setEmail] = useState(() => new URLSearchParams(location.search).get("email")?.slice(0, 254) ?? "");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const card = useRef<HTMLDivElement>(null);

  // A message the Google flow brought back; then clean the address.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const e = q.get("erro");
    if (e) setMsg(BACK_ERRORS[e] ?? BACK_ERRORS.google!);
    if (e || q.has("criar-conta") || q.has("email")) history.replaceState(null, "", location.pathname);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const shake = () => {
    const el = card.current;
    if (!el) return;
    el.classList.remove("shake");
    void el.offsetWidth;
    el.classList.add("shake");
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setStep(m === "signup" ? "dados" : "email");
    setErrors({});
    setMsg(null);
  };

  const fail = (err: unknown) => {
    if (err instanceof ApiError) {
      const by: Record<string, string> = {};
      for (const d of err.details) if (typeof d.path[0] === "string") by[d.path[0]] = d.message;
      setErrors(by);
      // A wrong code: its full message says how many attempts are left.
      setMsg(Object.keys(by).length && !by.code ? null : err.message);
    } else setMsg("Sem conexão. Tente de novo.");
    shake();
  };

  async function sendCode(again = false): Promise<boolean> {
    const bad: Record<string, string> = {};
    if (mode === "signup" && !name.trim()) bad.name = "Informe seu nome.";
    if (!emailOk(email.trim())) bad.email = "Confira o e-mail: falta algo como @loja.com.br.";
    setErrors(bad);
    setMsg(null);
    if (Object.keys(bad).length) {
      shake();
      return false;
    }
    setBusy("Enviando código…");
    try {
      const body = mode === "signup" ? { email: email.trim(), intent: "signup", name: name.trim() } : { email: email.trim() };
      await api("POST", "/auth/code", body, { tenant: false });
      setToast(`${again ? "Novo código enviado" : "Código enviado"} para ${email.trim()}`);
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function verify(code: string): Promise<boolean> {
    setBusy("Conferindo…");
    setMsg(null);
    setErrors({});
    try {
      const r = await api<{ accessToken: string; created: boolean }>("POST", "/auth/code/verify", { email: email.trim(), code }, { tenant: false });
      setLeaving(true);
      setTimeout(() => onIn(r.accessToken), reduceMotion() ? 0 : 420);
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusy(null);
    }
  }

  const title = step === "codigo" ? "Digite o código" : mode === "login" ? "Entrar" : "Criar conta";
  const lead =
    step === "codigo"
      ? `Enviamos 6 dígitos para ${email.trim()}. Ele vale por 10 minutos.`
      : mode === "login"
        ? "Digite seu e-mail e enviamos um código de acesso. Sem senha."
        : "Comece com 14 dias grátis. Sem cartão e sem senha.";

  return (
    <div className="au">
      <div className={`au-card ${leaving ? "leaving" : ""}`} ref={card}>
        <Showcase head={HEAD[step]} />
        <section className="au-form">
          <div className="seg" role="tablist" aria-label="Entrar ou criar conta">
            <button type="button" aria-pressed={mode === "login"} onClick={() => switchMode("login")}>Entrar</button>
            <button type="button" aria-pressed={mode === "signup"} onClick={() => switchMode("signup")}>Criar conta</button>
          </div>
          <div>
            <h1>{title}</h1>
            <p className="au-lead">{lead}</p>
          </div>
          {mode === "signup" && <Steps at={step === "codigo" ? 1 : 0} />}
          {step === "codigo" ? (
            <CodeStep
              key={`${mode}:${email}`}
              busy={busy}
              error={msg}
              onVerify={verify}
              onResend={() => sendCode(true)}
              onBack={() => (setStep(mode === "signup" ? "dados" : "email"), setErrors({}), setMsg(null))}
            />
          ) : (
            <form
              className="au-fields"
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                if (await sendCode()) setStep("codigo");
              }}
            >
              {mode === "signup" && (
                <Field id="name" label="Seu nome" error={errors.name}>
                  <input id="name" autoComplete="name" placeholder="Como devemos te chamar" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                </Field>
              )}
              <Field id="email" label="E-mail" error={errors.email}>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="voce@sualoja.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus={mode === "login"}
                />
              </Field>
              {mode === "signup" && (
                <div className="au-trial">
                  <span>✦</span>
                  <span>
                    <b>14 dias grátis.</b> Depois, R$ 99 por mês no Pix.
                  </span>
                </div>
              )}
              {msg && <p className="au-msg" role="alert">{msg}</p>}
              <button className={`btn primary au-go ${busy ? "loading" : ""}`} disabled={!!busy}>
                {busy ?? (mode === "login" ? "Enviar código" : "Continuar")}
              </button>
              <div className="au-or">ou</div>
              <a className="btn au-google" href="/auth/google">
                <span className="gg" aria-hidden="true" />
                Continuar com Google
              </a>
              <p className="au-legal">
                {mode === "login" ? (
                  <>
                    Ainda não tem conta?{" "}
                    <button type="button" className="linkish" onClick={() => switchMode("signup")}>Crie em 1 minuto</button>.
                  </>
                ) : (
                  <>
                    Já tem conta?{" "}
                    <button type="button" className="linkish" onClick={() => switchMode("login")}>Entrar</button>. Ao continuar você aceita os termos de uso e a política de privacidade.
                  </>
                )}
              </p>
            </form>
          )}
        </section>
      </div>
      {toast && (
        <div className="toast" role="status">
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

export function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <label className={`au-f ${error ? "bad" : ""}`} htmlFor={id}>
      {label}
      {children}
      {error && <span className="err">{error}</span>}
    </label>
  );
}

export function Steps({ at }: { at: number }) {
  const names = ["Seus dados", "Código", "Sua loja"];
  return (
    <div className="au-steps" aria-label={`Passo ${at + 1} de 3`}>
      {names.map((n, i) => (
        <span key={n} className={i < at ? "done" : i === at ? "on" : ""}>
          {i > 0 && <b />}
          <i>{i < at ? "✓" : i + 1}</i>
          {n}
        </span>
      ))}
    </div>
  );
}

/** Six boxes; typing, pasting and the phone's one-time-code suggestion all work. */
function CodeStep(props: {
  busy: string | null;
  error: string | null | undefined;
  onVerify: (code: string) => Promise<boolean>;
  onResend: () => Promise<boolean>;
  onBack: () => void;
}) {
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [ok, setOk] = useState(false);
  const [wait, setWait] = useState(30);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const code = digits.join("");

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const submit = async (c: string) => {
    if (c.length < 6 || props.busy) return;
    if (await props.onVerify(c)) setOk(true);
    else {
      setDigits(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    }
  };

  const fill = (from: number, text: string) => {
    const d = text.replace(/\D/g, "").slice(0, 6 - from).split("");
    if (!d.length) return;
    const next = [...digits];
    d.forEach((x, k) => (next[from + k] = x));
    setDigits(next);
    refs.current[Math.min(5, from + d.length)]?.focus();
    if (next.join("").length === 6) setTimeout(() => submit(next.join("")), 150);
  };

  return (
    <form
      className="au-fields"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit(code);
      }}
    >
      <div className={`au-otp ${props.error ? "bad" : ""} ${ok ? "ok" : ""}`} role="group" aria-label="Código de 6 dígitos">
        {digits.map((v, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={v}
            className={v ? "filled" : ""}
            style={{ ["--i" as string]: i }}
            inputMode="numeric"
            autoComplete={i ? "off" : "one-time-code"}
            aria-label={`Dígito ${i + 1}`}
            onChange={(e) => {
              const t = e.target.value.replace(/\D/g, "");
              if (t.length > 1) return fill(i, t);
              const next = [...digits];
              next[i] = t;
              setDigits(next);
              if (t && i < 5) refs.current[i + 1]?.focus();
              if (next.join("").length === 6) setTimeout(() => submit(next.join("")), 150);
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !v && i > 0) {
                const next = [...digits];
                next[i - 1] = "";
                setDigits(next);
                refs.current[i - 1]?.focus();
              }
              if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
              if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
            }}
            onPaste={(e) => {
              e.preventDefault();
              fill(0, e.clipboardData.getData("text"));
            }}
          />
        ))}
      </div>
      {props.error && <span className="au-otp-err" role="alert">{props.error}</span>}
      <button className={`btn primary au-go ${props.busy ? "loading" : ""}`} disabled={!!props.busy || code.length < 6}>
        {props.busy ?? "Confirmar"}
      </button>
      <div className="au-foot">
        <button type="button" className="linkish" onClick={props.onBack}>
          Trocar e-mail
        </button>
        {wait > 0 ? (
          <span>
            Reenviar em <span className="mono">0:{String(wait).padStart(2, "0")}</span>
          </span>
        ) : (
          <button type="button" className="linkish" onClick={async () => (await props.onResend()) && setWait(30)}>
            Reenviar código
          </button>
        )}
      </div>
      <p className="au-legal">Não chegou? Veja a caixa de spam. Se o e-mail não tiver conta, chega uma mensagem dizendo isso no lugar do código.</p>
    </form>
  );
}

/** The dark panel: a headline and three scenes of mockups, one per phrase of the ticker. */
export function Showcase({ head }: { head: ReactNode }) {
  const [tick, setTick] = useState(0);
  const [sales, setSales] = useState(0);
  const [shown, setShown] = useState<ReactNode>(head);
  const [swap, setSwap] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => (x + 1) % TICK.length), 3600);
    return () => clearInterval(t);
  }, []);

  // The headline fades out, changes, fades in.
  useEffect(() => {
    if (reduceMotion()) return setShown(head);
    setSwap(true);
    const t = setTimeout(() => (setShown(head), setSwap(false)), 220);
    return () => clearTimeout(t);
  }, [head]);

  // "Vendas hoje" counts up when its scene shows.
  useEffect(() => {
    if (tick !== 0) return;
    const to = 2624.8;
    if (reduceMotion()) return setSales(to);
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, Math.max(0, (t - t0 - 400) / 900));
      setSales(to * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [tick]);

  const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const fl = (i: number, pos: React.CSSProperties, child: ReactNode, opts: { hideSm?: boolean; delay?: number } = {}) => (
    <div className={`fl ${opts.hideSm ? "hide-sm" : ""}`} style={{ ...pos, ["--i" as string]: i }}>
      <div style={{ animationDelay: `${opts.delay ?? 0}s` }}>{child}</div>
    </div>
  );

  return (
    <section className="au-show" aria-hidden="true">
      <div className="holes" />
      <div className="brandrow">
        <span className="au-logo">V</span>Varejoon
      </div>
      <h2 className={swap ? "swap" : ""}>{shown}</h2>
      <div className="stage">
        <div className={`scene ${tick === 0 ? "on" : ""}`}>
          {fl(0, { left: "4%", top: "6%" }, <div className="tag"><small>Café Pilão 500g</small><b>R$ 18,90</b></div>)}
          {fl(
            1,
            { right: "2%", top: "2%" },
            <div className="mk mmetric">
              <small>Vendas hoje</small>
              <b>{brl(sales)}</b>
              <svg viewBox="0 0 200 34" preserveAspectRatio="none">
                <path className="a" d="M0 30 L28 26 L57 28 L85 20 L114 22 L142 14 L171 16 L200 4 L200 34 L0 34Z" />
                <path key={tick} className="l" d="M0 30 L28 26 L57 28 L85 20 L114 22 L142 14 L171 16 L200 4" />
              </svg>
            </div>,
            { hideSm: true, delay: -2 },
          )}
          {fl(
            2,
            { left: "2%", top: "42%" },
            <div className="mk mblocks">
              <small>Blocos</small>
              <span><Icon name="chart" size={14} />Métrica</span>
              <span className="on"><Icon name="table" size={14} />Tabela<i className="cur"><Icon name="cursor" size={20} /></i></span>
              <span><Icon name="plus" size={14} />Botão</span>
            </div>,
            { hideSm: true, delay: -4 },
          )}
          {fl(
            3,
            { right: "2%", top: "60%" },
            <div className="mk mtable">
              <small>Pedidos de hoje</small>
              <div><b>#1042</b><span>Padaria Pão Quente</span><em className="ok">Pago</em></div>
              <div><b>#1041</b><span>Ana Souza</span><em>Separado</em></div>
              <div><b>#1040</b><span>Mercearia do Zé</span><em className="ok">Pago</em></div>
            </div>,
            { hideSm: true, delay: -1 },
          )}
        </div>
        <div className={`scene ${tick === 1 ? "on" : ""}`}>
          {fl(0, { right: "4%", top: "2%" }, <div className="mubub">Crie uma tela de fechamento de caixa</div>)}
          {fl(
            1,
            { left: "3%", top: "20%" },
            <div className="mk mdiff">
              <small>Proposta · 3 mudanças</small>
              <span>+ tela Fechamento de caixa</span>
              <span>+ bloco total por pagamento</span>
              <span>+ botão Fechar caixa</span>
            </div>,
            { hideSm: true, delay: -3 },
          )}
          {fl(
            2,
            { left: "4%", top: "66%" },
            <div className="mk mflow">
              <span className="nd"><i className="mar"><Icon name="workflows" size={13} /></i>Pedido pago</span>
              <span className="ln" />
              <span className="nd"><i className="jab"><Icon name="bell" size={13} /></i>Avisar dono</span>
            </div>,
            { hideSm: true, delay: -4 },
          )}
          {fl(3, { right: "5%", top: "46%" }, <div className="mappr">✓ Aprovar mudanças</div>, { delay: -1 })}
          {fl(4, { right: "3%", top: "86%" }, <div className="mai"><b>✦ IA:</b> pronto, a tela já está no menu.</div>, { hideSm: true, delay: -2 })}
        </div>
        <div className={`scene ${tick === 2 ? "on" : ""}`}>
          {fl(0, { left: "4%", top: "6%" }, <div className="mk mchip"><span className="ok">✓</span>Pix recebido · R$ 742,50</div>)}
          {fl(1, { right: "3%", top: "20%" }, <div className="mk minv"><small>Fatura do mês</small><b>R$ 99,00</b><em>✓ Paga no Pix</em></div>, { hideSm: true, delay: -2 })}
          {fl(2, { left: "6%", top: "40%" }, <div className="mk mqr"><span /><small>Pix copia e cola</small></div>, { hideSm: true, delay: -4 })}
          {fl(3, { right: "4%", top: "62%" }, <div className="mtrial"><b>14</b><span>dias grátis<br /><small>sem cartão</small></span></div>, { delay: -1 })}
        </div>
      </div>
      <div className="ticker">
        <span key={tick}>{TICK[tick]}</span>
        <span className="dots">
          {TICK.map((_, i) => (
            <i key={i} className={i === tick ? "on" : ""} />
          ))}
        </span>
      </div>
    </section>
  );
}
